package com.evoapps.evoshift.ui.finance

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.UploadFile
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import com.evoapps.evoshift.domain.salesimport.SalesSheetParser
import com.evoapps.evoshift.salesimport.IncomingSalesFile
import com.evoapps.evoshift.salesimport.months
import com.evoapps.evoshift.ui.util.monthName
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.incoming_sales_all_months
import evoshift.composeapp.generated.resources.incoming_sales_month
import evoshift.composeapp.generated.resources.incoming_sales_no_access
import evoshift.composeapp.generated.resources.incoming_sales_preview
import evoshift.composeapp.generated.resources.incoming_sales_text
import evoshift.composeapp.generated.resources.incoming_sales_title
import evoshift.composeapp.generated.resources.workplace
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.datetime.YearMonth
import kotlinx.datetime.number
import org.jetbrains.compose.resources.stringResource

/** Test tags of [IncomingSalesFileDialog]. */
object IncomingSalesFileTags {
    const val DIALOG = "incoming_sales_dialog"
    const val WORKPLACE = "incoming_sales_workplace"
    const val MONTH = "incoming_sales_month"
}

/**
 * SH-161 F4: a sales file opened with the app ("Open with", "Share"): which workplace's takings it
 * is and, for a file of several months, which month (or all of them); then the Sales import's
 * preview of that workplace (nothing is written before "Import N days" there). [workplaces]: the
 * person's workplaces whose finances they see (`salesImportWorkplaces`); with none, the dialog
 * only says who can import it.
 */
@Composable
fun IncomingSalesFileDialog(
    file: IncomingSalesFile,
    workplaces: List<Pair<String, String>>,
    currentWorkplaceId: String?,
    onImport: (workplaceId: String, month: YearMonth?) -> Unit,
    onDismiss: () -> Unit
) {
    if (workplaces.isEmpty()) {
        EvoDialog(
            title = stringResource(Res.string.incoming_sales_title),
            icon = Icons.Default.UploadFile,
            subtitle = stringResource(Res.string.incoming_sales_no_access),
            onDismiss = onDismiss,
            dismiss = DialogAction(stringResource(Res.string.close), onDismiss),
            modifier = Modifier.testTag(IncomingSalesFileTags.DIALOG)
        ) {}
        return
    }
    var workplaceId by remember(workplaces) {
        mutableStateOf(workplaces.firstOrNull { it.first == currentWorkplaceId }?.first ?: workplaces.first().first)
    }
    // The file's months, read off the main thread (an unreadable file has none: the import says why).
    val months by produceState(emptyList<YearMonth>(), file) {
        val bytes = file.bytes ?: return@produceState
        value = withContext(Dispatchers.Default) { runCatching { SalesSheetParser.parse(bytes).months() }.getOrDefault(emptyList()) }
    }
    var month by remember(file) { mutableStateOf<YearMonth?>(null) }
    EvoDialog(
        title = stringResource(Res.string.incoming_sales_title),
        icon = Icons.Default.UploadFile,
        subtitle = stringResource(Res.string.incoming_sales_text, file.name),
        onDismiss = onDismiss,
        confirm = DialogAction(stringResource(Res.string.incoming_sales_preview), { onImport(workplaceId, month?.takeIf { it in months }) }),
        modifier = Modifier.testTag(IncomingSalesFileTags.DIALOG)
    ) {
        Text(file.name, color = Color.White)
        if (workplaces.size > 1) {
            EvoDropdown(
                label = stringResource(Res.string.workplace),
                options = workplaces.map { it.first },
                selected = workplaceId,
                onSelected = { workplaceId = it },
                optionLabel = { id -> workplaces.firstOrNull { it.first == id }?.second ?: id },
                modifier = Modifier.testTag(IncomingSalesFileTags.WORKPLACE)
            )
        }
        if (months.size > 1) {
            val allMonths = stringResource(Res.string.incoming_sales_all_months)
            EvoDropdown(
                label = stringResource(Res.string.incoming_sales_month),
                options = listOf<YearMonth?>(null) + months,
                selected = month,
                onSelected = { month = it },
                optionLabel = { option -> option?.let { "${monthName(it.month.number)} ${it.year}" } ?: allMonths },
                placeholder = allMonths,
                modifier = Modifier.testTag(IncomingSalesFileTags.MONTH)
            )
        }
    }
}
